import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../../components/dialog'
import { cn } from '../../lib/utils'

export interface UIDialogProps extends React.ComponentProps<typeof Dialog> {
  variant?: 'default' | 'modal' | 'drawer'
}

export interface UIDialogContentProps extends React.ComponentProps<typeof DialogContent> {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  variant?: 'default' | 'elevated' | 'minimal'
  spacing?: 'tight' | 'normal' | 'loose'
}

export interface UIDialogHeaderProps extends React.ComponentProps<typeof DialogHeader> {
  variant?: 'default' | 'compact' | 'spacious'
}

export interface UIDialogTitleProps extends React.ComponentProps<typeof DialogTitle> {
  size?: 'sm' | 'md' | 'lg'
}

export interface UIDialogDescriptionProps extends React.ComponentProps<typeof DialogDescription> {
  variant?: 'default' | 'muted' | 'subtle'
}

export interface UIDialogFooterProps extends React.ComponentProps<typeof DialogFooter> {
  variant?: 'default' | 'actions' | 'status'
  justify?: 'start' | 'center' | 'end' | 'between'
}

/**
 * Enhanced Dialog component following design system best practices
 * Provides consistent dialog/modal styling and behavior
 */
export const UIDialog = ({ variant = 'default', ...props }: UIDialogProps) => {
  return (
    <Dialog
      {...props}
    />
  )
}

export const UIDialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogContent>,
  UIDialogContentProps
>(({
  className,
  size = 'md',
  variant = 'default',
  spacing = 'normal',
  ...props
}, ref) => {
  return (
    <DialogContent
      ref={ref}
      className={cn(
        // Reset base padding — composed spacing variants control all padding
        'p-0',

        // Base styles
        'z-50',
        'border border-gray-200 dark:border-gray-800',
        'ring-1 ring-white/10 dark:ring-white/5',

        // Size styles
        {
          'max-w-sm': size === 'sm',
          'max-w-lg': size === 'md',
          'max-w-2xl': size === 'lg',
          'max-w-4xl': size === 'xl',
          'max-w-[95vw] max-h-[95vh]': size === 'full',
        },

        // Variant styles with glassmorphism enhancements
        {
          'gap-4 bg-background/95 dark:bg-gray-900/90 shadow-lg shadow-gray-950/10 dark:shadow-black/30': variant === 'default',
          'gap-6 bg-background/98 dark:bg-gray-900/95 shadow-2xl shadow-gray-950/15 dark:shadow-black/40 ring-2 ring-white/20 dark:ring-white/10': variant === 'elevated',
          'gap-3 shadow-sm border-gray-100 dark:border-gray-900 bg-background/90 dark:bg-gray-900/80': variant === 'minimal',
        },

        // Spacing styles (sole source of padding, base p-6 is reset above)
        {
          'p-4': spacing === 'tight',
          'p-6': spacing === 'normal',
          'p-8': spacing === 'loose',
        },

        className
      )}
      {...props}
    />
  )
})

export const UIDialogHeader = React.forwardRef<
  React.ComponentRef<typeof DialogHeader>,
  UIDialogHeaderProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <DialogHeader
      ref={ref}
      className={cn(
        {
          'space-y-1.5': variant === 'default',
          'space-y-1': variant === 'compact',
          'space-y-3': variant === 'spacious',
        },
        className
      )}
      {...props}
    />
  )
})

export const UIDialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogTitle>,
  UIDialogTitleProps
>(({ className, size = 'md', ...props }, ref) => {
  return (
    <DialogTitle
      ref={ref}
      className={cn(
        {
          'text-base font-semibold': size === 'sm',
          'text-lg font-semibold': size === 'md',
          'text-xl font-semibold': size === 'lg',
        },
        className
      )}
      {...props}
    />
  )
})

export const UIDialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogDescription>,
  UIDialogDescriptionProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <DialogDescription
      ref={ref}
      className={cn(
        {
          'text-sm text-muted-foreground': variant === 'default',
          'text-sm text-muted-foreground/80': variant === 'muted',
          'text-xs text-muted-foreground/60': variant === 'subtle',
        },
        className
      )}
      {...props}
    />
  )
})

export const UIDialogFooter = React.forwardRef<
  React.ComponentRef<typeof DialogFooter>,
  UIDialogFooterProps
>(({ className, variant = 'default', justify = 'end', ...props }, ref) => {
  return (
    <DialogFooter
      ref={ref}
      className={cn(
        // Base styles
        'gap-3',

        // Variant styles with glassmorphism
        {
          'pt-6 border-t border-gray-200 dark:border-gray-800': variant === 'actions',
          'bg-muted/40 dark:bg-gray-900/30 py-4 mt-6 border-t border-gray-200 dark:border-gray-800': variant === 'status',
        },

        // Justify styles
        {
          'justify-start': justify === 'start',
          'justify-center': justify === 'center',
          'justify-end': justify === 'end',
          'justify-between': justify === 'between',
        },

        className
      )}
      {...props}
    />
  )
})

// Re-export other dialog components with consistent naming
export const UIDialogTrigger = DialogTrigger

UIDialog.displayName = "UIDialog"
UIDialogContent.displayName = "UIDialogContent"
UIDialogHeader.displayName = "UIDialogHeader"
UIDialogTitle.displayName = "UIDialogTitle"
UIDialogDescription.displayName = "UIDialogDescription"
UIDialogFooter.displayName = "UIDialogFooter"